import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Users, Target, CheckCircle, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// AJ's actual images
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_a68a521f-d588-4c7a-99ce-4eb4c9c2d03b/artifacts/blvr559f_IMG_3348.jpeg";
const COACH_IMAGE = "https://customer-assets.emergentagent.com/job_fit-life-coach/artifacts/z5x3jaiu_6957FE8B-9790-4174-A272-97B5877E41F2.png";
const GROUP_IMAGE = "https://customer-assets.emergentagent.com/job_a68a521f-d588-4c7a-99ce-4eb4c9c2d03b/artifacts/s50xz4s0_c681515e-c9a8-48a3-8db6-282e392fd3df.jpeg";

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    setIsVisible(true);
  }, [setIsVisible]);

  useEffect(() => {
    fetch(`${API_URL}/api/content/testimonials`)
      .then(res => res.json())
      .then(setTestimonials)
      .catch(() => {});
  }, []);

  const features = [
    {
      icon: Target,
      title: 'Realistic Goals',
      description: 'Training programs designed around your life, not the other way around.'
    },
    {
      icon: Users,
      title: 'Expert Coaching',
      description: 'Experienced guidance from AJ Javier, helping you build lasting strength.'
    },
    {
      icon: CheckCircle,
      title: 'Proven Results',
      description: 'Clients who stick with training and see real, sustainable progress.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - LIFT Style with Original Content */}
      <section className="relative min-h-screen flex items-center justify-center">
        {/* Background Image - AJ running */}
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE}
            alt="AJ Javier - Personal Trainer"
            className="w-full h-full object-cover object-top md:object-[center_20%]"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>

        {/* Hero Content */}
        <div className={`relative z-10 container text-center text-white transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-2 mb-6">
            <MapPin size={18} className="text-[#C9A962]" />
            <span className="text-sm font-medium tracking-wide">East London, Leyton</span>
          </div>
          
          <h1 className="heading-display text-white mb-4">
            Personal Training
          </h1>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium tracking-wide text-[#C9A962] mb-8 uppercase" style={{fontFamily: "'Oswald', sans-serif"}}>
            That Fits Real Life
          </h2>
          
          <p className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Build strength, confidence, and sustainable habits with coaching that works around your schedule. No extreme diets. No unrealistic routines. Just effective training that lasts.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Link to="/contact" className="btn-gold">
              Book Free Consultation
            </Link>
            <Link to="/services" className="btn-outline-light">
              View Services
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-white/70">
            <span className="flex items-center gap-2">
              <CheckCircle size={16} className="text-[#C9A962]" />
              Anytime Fitness Leyton
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle size={16} className="text-[#C9A962]" />
              Select Gyms
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle size={16} className="text-[#C9A962]" />
              Outdoor
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle size={16} className="text-[#C9A962]" />
              In-Home
            </span>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="heading-large mb-6">Welcome to Heft Training</h2>
              <p className="body-large mb-6">
                Heft Training is a coaching-led personal training service based in East London. 
                Led by Head Coach AJ Javier, we help busy adults, professionals, and parents 
                feel stronger, leaner, and more confident through realistic, sustainable training.
              </p>
              <Link to="/about" className="btn-outline">
                Meet AJ Javier
              </Link>
            </div>
            <div className="relative">
              <img 
                src={COACH_IMAGE}
                alt="AJ Javier - Head Coach"
                className="w-full aspect-[4/5] object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-[#F5F0EB] py-20 md:py-28">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-large mb-4">Why Choose Heft Training</h2>
            <p className="body-large max-w-2xl mx-auto">
              Training should enhance your life, not complicate it. Here's what makes our approach different.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div key={feature.title} className="bg-white p-8 text-center">
                <div className="w-16 h-16 bg-[#C9A962] flex items-center justify-center mx-auto mb-6">
                  <feature.icon size={28} className="text-[#1A1A1A]" />
                </div>
                <h3 className="heading-small mb-3">{feature.title}</h3>
                <p className="body-regular text-[#6B6B6B]">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="heading-large mb-8">Our Philosophy</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-[#C9A962] flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={20} className="text-[#1A1A1A]" />
                  </div>
                  <div>
                    <h4 className="heading-small mb-2">Training Fits Your Life</h4>
                    <p className="body-regular text-[#6B6B6B]">Not the other way around. We work with your schedule, not against it.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-[#C9A962] flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={20} className="text-[#1A1A1A]" />
                  </div>
                  <div>
                    <h4 className="heading-small mb-2">Consistency Over Perfection</h4>
                    <p className="body-regular text-[#6B6B6B]">Small, sustainable changes create lasting results. No extreme approaches.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-[#C9A962] flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={20} className="text-[#1A1A1A]" />
                  </div>
                  <div>
                    <h4 className="heading-small mb-2">Strength-Based Results</h4>
                    <p className="body-regular text-[#6B6B6B]">Building strength is the foundation for feeling confident and capable.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src={GROUP_IMAGE}
                alt="Heft Training clients at HYROX"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="bg-[#F5F0EB] py-20 md:py-28">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-large mb-4">Client Stories</h2>
            <p className="body-regular text-[#6B6B6B]">Real results from real people</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((testimonial) => (
              <div key={testimonial.id} className="bg-white p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={`star-${i}`} size={14} fill="#C9A962" className="text-[#C9A962]" />
                  ))}
                </div>
                <p className="font-serif text-lg italic text-[#2D2D2D] mb-6">"{testimonial.content}"</p>
                <div className="flex items-center justify-between border-t border-[#E5E0DB] pt-4">
                  <div>
                    <p className="font-semibold text-sm text-[#1A1A1A]">{testimonial.name}</p>
                    <p className="text-xs text-[#6B6B6B]">{testimonial.role}</p>
                  </div>
                  <span className="text-xs font-semibold tracking-[0.1em] uppercase text-[#C9A962]">
                    {testimonial.duration}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/results" className="btn-outline">
              View All Results
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="relative py-24 md:py-32"
        style={{
          backgroundImage: `url(${COACH_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
        }}
      >
        <div className="absolute inset-0 bg-black/70" />
        <div className="container relative z-10 text-center">
          <h2 className="heading-display text-white mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="font-serif text-xl text-white/90 mb-10 max-w-xl mx-auto italic">
            Book a free consultation with AJ to discuss your goals and see how Heft Training can help you build the strength and confidence you're looking for.
          </p>
          <Link to="/contact" className="btn-gold inline-flex items-center gap-2">
            Book Free Consultation
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
